import { zodResolver } from '@hookform/resolvers/zod';
import {
  InvalidateQueryFilters,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useEnv } from '@/components/providers/env-provider';
import { UserType } from '@/shared-types/roleTypes';
import Fetch from '@/services/fetch/Fetch';
import { useUser } from '@/components/providers/user-provider';
import { envToBaseUrl } from '@/utils/env';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const NONE_VALUE = 'none';

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'This field has to be filled.' })
    .email('This is not a valid email.'),
  role: z.string(),
  marketRestrictions: z.array(z.string()),
});

function ReviewUserAccessRequest() {
  const { env } = useEnv();
  const listUsersEndpoint = `${envToBaseUrl(env)}/userAccess/list-user-access-requests`;
  const removeUserEndpoint = `${envToBaseUrl(env)}/userAccess/remove-user-access-requests`;

  const navigate = useNavigate();
  const [loading, setLoading] = useState('');
  const [deleteLoading, setDeleteLoading] = useState('');
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      role: '',
      marketRestrictions: [],
    },
  });
  const { role } = useUser();

  const { data } = useQuery<UserType[]>({
    queryKey: ['list_user_access_request'],
    queryFn: () => Fetch.json(listUsersEndpoint),
    refetchOnWindowFocus: false,
  });

  const queryClient = useQueryClient();

  const deleteAndcreateUserMutation = useMutation({
    mutationFn: async (form: z.infer<typeof formSchema>) => {
      setLoading(form.email);

      // First delete the pending user access request in firestore
      await Fetch.json(removeUserEndpoint, {
        method: 'POST',
        body: JSON.stringify({ email: form.email, sendNotification: false }),
      });

      // Then create user in roles table
      const dataToSend = {
        ...form,
        marketRestrictions: form.marketRestrictions.includes(NONE_VALUE)
          ? []
          : form.marketRestrictions,
      };

      await Fetch.json(`${envToBaseUrl(env)}/roles/create`, {
        method: 'POST',
        body: JSON.stringify(dataToSend),
      });
    },
    onSuccess: async () => {
      toast.success('Successfully created new user');

      await queryClient.invalidateQueries([
        'list_user_access_request',
      ] as InvalidateQueryFilters);
    },
    onError: () => {
      toast.error('Failed to create user');
    },
    onSettled: () => {
      setLoading('');
      form.reset({});
    },
  });

  const deletePendingUser = useMutation({
    mutationFn: async (email: string) => {
      setDeleteLoading(email);

      await Fetch.json(removeUserEndpoint, {
        method: 'POST',
        body: JSON.stringify({ email: email }),
      });
    },
    onSuccess: async () => {
      toast.success('Successfully deleted pending user');

      await queryClient.invalidateQueries([
        'list_user_access_request',
      ] as InvalidateQueryFilters);
    },
    onError: () => {
      toast.error('Failed to delete pending user');
    },
    onSettled: () => {
      setDeleteLoading('');
      form.reset({});
    },
  });

  function handleDeleteAndCreateUser(data: z.infer<typeof formSchema>) {
    deleteAndcreateUserMutation.mutate(data);
  }

  function handleDeletePendingUser(email: string) {
    deletePendingUser.mutate(email);
  }

  if (role !== 'admin') {
    navigate('/');
  }

  return (
    <>
      <h1 className='scroll-m-20 font-semibold tracking-tight lg:text-2xl mb-6'>
        Review pending user applications
      </h1>
      <div className='rounded-md border'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='w-[100px]'>User</TableHead>
              <TableHead>Market restriction</TableHead>
              <TableHead>Role</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.map((role, index) => (
              <TableRow key={`${role.email}-${index}`}>
                <TableCell className='font-medium'>{role.email}</TableCell>
                <TableCell>
                  {role.role === 'editor' && !role?.markets
                    ? 'None'
                    : role.markets?.join(', ')}
                </TableCell>
                <TableCell>{role.role}</TableCell>
                <TableCell className='items-center flex'>
                  <Button
                    variant='default'
                    onClick={() => {
                      handleDeleteAndCreateUser({
                        email: role.email,
                        role: role.role,
                        marketRestrictions: role.markets || [],
                      });
                    }}
                    className='mr-2'
                    isLoading={loading === role.email}
                  >
                    Approve
                  </Button>
                  <Button
                    variant='default'
                    onClick={() => {
                      handleDeletePendingUser(role.email);
                    }}
                    isLoading={deleteLoading === role.email}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

export default ReviewUserAccessRequest;
